<template>
  <div>
    <data-table
      ref="datatableRef"
      test-id-prefix="users"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick"
      @toggle-change="handleToggleChange"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import { config } from '@/services/configService';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
}

interface UserRow {
  sub?: Id;
  username?: string;
  name?: string;
  email?: string;
  enabled?: boolean | 0 | 1;
  groups?: string[];
  franquias?: string[];
  [key: string]: unknown;
}

interface ToggleChangePayload {
  column: string;
  rowData: UserRow;
  newValue: boolean;
}

interface ButtonClickPayload {
  value?: Id;
  data: UserRow;
  action: string;
}

const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Emits
const emit = defineEmits<{
  'edit': [user: UserRow];
  'disable': [user: UserRow];
  'enable': [user: UserRow];
  'change-password': [user: UserRow];
  'delete': [user: UserRow];
}>();

// Computed properties
const endpoint = computed<string>(() => config.api.sistemaHost + '/usuarios/pegaUsuarios');

// Define user action handlers before tableColumns
const disableUser = (user: UserRow): void => {
  log('UsersTable', 'disableUser', 'Disabling user', user);
  emit('disable', user);
};

const enableUser = (user: UserRow): void => {
  log('UsersTable', 'enableUser', 'Enabling user', user);
  emit('enable', user);
};

const changePassword = (user: UserRow): void => {
  log('UsersTable', 'changePassword', 'Change password for user', user);
  emit('change-password', user);
};

const deleteUser = (user: UserRow): void => {
  log('UsersTable', 'deleteUser', 'Delete user requested', user);
  emit('delete', user);
};

const tableColumns = computed(() => {
  return [
    {
      data: 'sub',
      title: t('ID'),
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id'
    },
    {
      data: 'enabled',
      title: t('Status'),
      width: 100,
      resizable: true,
      sortable: true,
      renderType: 'toggle'
    },
    {
      data: 'username',
      title: t('Usuário'),
      width: 150,
      resizable: true,
      sortable: true
    },
    {
      data: 'name',
      title: t('Nome'),
      width: 250,
      resizable: true,
      sortable: true
    },

    {
      data: 'groups',
      title: t('Grupos'),
      width: 250,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): Node => {
        const container = document.createElement('div');
        container.className = 'tag-container';

        if (!params.value || !Array.isArray(params.value) || params.value.length === 0) {
          container.innerHTML = '<span class="p-tag p-tag-secondary">-</span>';
          return container;
        }

        const tags = (params.value as string[]).map(group => {
          return `<span class="p-tag p-tag-info">${group}</span>`;
        }).join(' ');

        container.innerHTML = tags;
        return container;
      }
    },
    {
      data: 'franquias',
      title: t('Franquias'),
      width: 300,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): Node => {
        const container = document.createElement('div');
        container.className = 'tag-container';

        if (!params.value || !Array.isArray(params.value) || params.value.length === 0) {
          container.innerHTML = '<span class="p-tag p-tag-secondary">-</span>';
          return container;
        }

        const tags = (params.value as string[]).map(franquia => {
          return `<span class="p-tag p-tag-primary">${franquia}</span>`;
        }).join(' ');

        container.innerHTML = tags;
        return container;
      }
    },
    {
      data: null,
      title: t('Ações'),
      width: 100,
      resizable: false,
      sortable: false,
      frozen: true,
      alignFrozen: 'right',
      renderType: 'action_buttons',
      buttons: [
        {
          icon: 'pi pi-key',
          class: 'p-button-info p-button-sm',
          tooltip: t('Trocar senha'),
          action: 'change-password',
          testId: (row: UserRow): string => `users-row-${row.sub}-change-password`
        },
        {
          icon: 'pi pi-trash',
          class: 'p-button-danger p-button-outlined p-button-sm',
          tooltip: t('Excluir'),
          action: 'delete',
          testId: (row: UserRow): string => `users-row-${row.sub}-delete`
        }
      ]
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaUsers',
    title: t('Usuários'),
    filename: `users_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    footer: false,
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => ({
  enabled: true,
  columns: {
    'username': 'text',
    'name': 'text',
    'email': 'text',
    'phone_number': 'text',
    'enabled': 'select',
    'groups': 'text',
    'franquias': 'text'
  }
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV')
}));

// Methods
const loadData = (): void => {
  const stack = new Error().stack;
  log('UsersTable', 'loadData', 'Triggering data reload', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('UsersTable', 'handleDataLoaded', 'Data loaded successfully', data?.length);
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('UsersTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar usuários'));
};

const handleToggleChange = ({ column, rowData, newValue }: ToggleChangePayload): void => {
  log('UsersTable', 'handleToggleChange', 'Toggle changed', { column, rowData, newValue });
  if (newValue) {
    enableUser(rowData);
  } else {
    disableUser(rowData);
  }
};

const handleButtonClick = ({ value, data, action }: ButtonClickPayload): void => {
  log('UsersTable', 'handleButtonClick', 'Button clicked', { value, data, action });

  if (action === 'change-password') {
    changePassword(data);
  } else if (action === 'delete') {
    deleteUser(data);
  }
};

const handleEditClick = (userId: Id): void => {
  log('UsersTable', 'handleEditClick', 'Edit clicked for user ID', userId);
  // Emit only the user ID - modal/form will fetch the full data
  emit('edit', { sub: userId });
};

const handleRefresh = (): void => {
  log('UsersTable', 'handleRefresh', 'Refresh event received');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('UsersTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('UsersTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  // Listen for filter updates and refresh events
  eventBus.on('filter.updated', handleRefresh);
  eventBus.on('users.refresh', handleRefresh);
});

onBeforeUnmount(() => {
  log('UsersTable', 'beforeUnmount', 'Component unmounting');
  eventBus.off('filter.updated', handleRefresh);
  eventBus.off('users.refresh', handleRefresh);
});

// Expose for parent
defineExpose({
  loadData
});
</script>

<style scoped>
/* PrimeVue Tag Styling */
:deep(.tag-container) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

:deep(.p-tag) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 4px;
  white-space: nowrap;
}

:deep(.p-tag-success) {
  background-color: #22c55e;
  color: #ffffff;
}

:deep(.p-tag-danger) {
  background-color: #ef4444;
  color: #ffffff;
}

:deep(.p-tag-info) {
  background-color: #3b82f6;
  color: #ffffff;
}

:deep(.p-tag-primary) {
  background-color: #6366f1;
  color: #ffffff;
}

:deep(.p-tag-secondary) {
  background-color: #64748b;
  color: #ffffff;
}

/* PrimeVue Button Styling for AG Grid */
:deep(.action-buttons) {
  display: flex;
  gap: 4px;
  align-items: center;
}

:deep(.p-button) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

/* O `border: none` acima apagaria o contorno de um botão outlined. A cor vem do tema. */
:deep(.p-button-outlined) {
  border-style: solid;
  border-width: 1px;
}

:deep(.p-button-sm) {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

:deep(.p-button i) {
  font-size: 0.875rem;
}

:deep(.p-button-warning) {
  background-color: #f59e0b;
  color: #ffffff;
}

:deep(.p-button-warning:hover) {
  background-color: #d97706;
}

:deep(.p-button-success) {
  background-color: #22c55e;
  color: #ffffff;
}

:deep(.p-button-success:hover) {
  background-color: #16a34a;
}

:deep(.p-button-info) {
  background-color: #3b82f6;
  color: #ffffff;
}

:deep(.p-button-info:hover) {
  background-color: #2563eb;
}

:deep(.p-button-secondary) {
  background-color: #64748b;
  color: #ffffff;
}

:deep(.p-button-secondary:hover) {
  background-color: #475569;
}
</style>
